import React from 'react';
import { Link } from 'react-router-dom';
import { Field } from 'formik';
import { FormCombobox } from '@/components/custom/form';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';

const appName = process.env.REACT_APP_NAME;

const envWithoutTerms = ['sett-solutions', 'aprovavp', 'floriano-educa-7'];

const termsText = envWithoutTerms.includes(appName)
  ? 'Em breve...'
  : `O https://plataforma.canaleducacao.tv/(“Website”) é pertencente e operado pela HF TECNOLOGIA LTDA, pessoa jurídica de direito privado, inscrita no CNPJ/MF sob o nº 40.432.531/0001-78, com sede no SCN Quadra 05, Brasília Shopping and Towers, Bloco A, Sala 118, CEP: 70.715-900. Você pode entrar em contato conosco pelo e-mail suporte@suporte-plataforma.com.br.
2. A HF TECNOLOGIA, por meio do Website e do Aplicativo, provê uma plataforma educacional para alunos, professores e demais atuantes do ecossistema educacional.
3. Estes Termos de Uso, em conjunto com os demais contratos e a nossa Política de Privacidade, estabelecem os termos e condições aplicáveis à utilização de nosso Website. É muito importante que você leia e compreenda estas regras, bem como outras regras que possam ser aplicáveis, incluindo aquelas advindas da Lei nº 8.078/1990 (“Código de Defesa do Consumidor”).
4. Para desfrutar das funcionalidades e para acessar as áreas do Website e do Aplicativo, será requerido que VOCÊ confirme expressamente sua anuência com as Condições Gerais (Parte I abaixo) e os Termos e Condições Adicionais (Parte II abaixo) antes do seu primeiro acesso.`;

function FormTerms() {
  return (
    <Textarea
      className={cn(
        'terms',
        'c-auth-form-terms',
        'flex',
        'p-4',
        'rounded-[10px]',
        'border',
        'border-solid',
        'border-neutral-lightest',
        'bg-white',
        'shadow-[0px_4px_10px_0px_rgba(93,106,134,0.05)]',
        'text-neutral-darker',
        'font-[Nunito]',
        'text-xs',
        'font-light',
        'leading-[150%]',
        'resize-none',
        'w-full',
        'h-60'
      )}
      rows={5}
      defaultValue={termsText}
      readOnly
    />
  );
}

function FormCustomSelect(props) {
  const { name, options = [], values, placeholder = 'Pesquise', onChange, ...restProps } = props;

  return (
    <Field
      name={name}
      {...restProps}
    >
      {({ form }) => {
        const rawValue = values?.[name];

        const normalizedValue = typeof rawValue === 'object' ? rawValue?.value : (rawValue ?? null);

        return (
          <FormCombobox
            options={options}
            value={normalizedValue}
            placeholder={placeholder}
            onValueChange={value => {
              form.setFieldValue(name, value);

              if (onChange) onChange(value);
            }}
          />
        );
      }}
    </Field>
  );
}

function FormFieldGradeWrapper(props) {
  const { children, ...restProps } = props;

  return (
    <div
      className={cn('fieldGradeWrapper', 'c-auth-form-field-grade-wrapper', 'grid', 'grid-cols-2', 'gap-8')}
      {...restProps}
    >
      {children}
    </div>
  );
}

function FormFieldGrade(props) {
  const { children, modifiers, ...restProps } = props;

  const isFirstGrade = modifiers?.includes('firstGrade');
  const isSecondGrade = modifiers?.includes('secondGrade');
  const isThirdGrade = modifiers?.includes('thirdGrade');
  const isEja = modifiers?.includes('eja');

  return (
    <label
      className={cn(
        'fieldGrade',
        'c-auth-form-field-grade',
        'cursor-pointer',
        'rounded-lg',
        'border-l-[10px]',
        'border-l-[#b9b9b9]',
        'bg-white',
        'shadow-[0px_4px_10px_0px_rgba(93,106,134,0.05)]',
        'text-black',
        'text-xl',
        'font-bold',
        // input hidden + default checked+span gray highlight
        '[&_input]:hidden',
        '[&_input:checked+span]:bg-[#ccc]',
        // span layout
        '[&_span]:flex',
        '[&_span]:items-center',
        '[&_span]:justify-center',
        '[&_span]:w-full',
        '[&_span]:h-full',
        '[&_span]:min-h-[128px]',
        '[&_span]:p-5',
        '[&_span]:rounded-[0_8px_8px_0]',
        // grade variants: preserve test class names + per-variant accent colors
        isFirstGrade && 'firstGrade border-l-[#8ea7e4] [&_input:checked+span]:bg-[#8ea7e4]',
        isSecondGrade && 'secondGrade border-l-[#fdb462] [&_input:checked+span]:bg-[#fdb462]',
        isThirdGrade && 'thirdGrade border-l-[#006b30] [&_input:checked+span]:bg-[#006b30] [&_input:checked+span]:text-white',
        isEja && 'eja border-l-[#254aa5] [&_input:checked+span]:bg-[#254aa5] [&_input:checked+span]:text-white'
      )}
      {...restProps}
    >
      {children}
    </label>
  );
}

function FormStep(props) {
  const { children, show } = props;

  if (show) return <>{children}</>;
  return null;
}

function FormFeedback(props) {
  const { children, valid, invalid } = props;

  return <div className={cn('feedback', 'c-auth-form-feedback', 'text-sm', 'font-light', valid && 'valid text-success', invalid && 'invalid text-danger')}>{children}</div>;
}

function FormLink(props) {
  const { to = '/', children } = props;

  return (
    <Link
      className={cn('link', 'c-auth-form-link', 'self-start', 'text-neutral', 'text-sm', 'font-light', 'no-underline', 'hover:underline', 'focus:underline')}
      to={to}
    >
      {children}
    </Link>
  );
}

function FormField(props) {
  const { children, round } = props;

  return (
    <label
      className={cn(
        'field',
        'c-auth-form-field',
        'flex',
        'items-start',
        'gap-1',
        'text-neutral-darker',
        'text-sm',
        'font-light',
        'select-none',
        // anchor styles inside field
        '[&_a]:font-bold',
        '[&_a]:no-underline',
        '[&_a:hover]:underline',
        '[&_a:focus]:underline',
        round && 'round [&_input[type=checkbox]]:rounded-full'
      )}
    >
      {children}
    </label>
  );
}

const FormControl = React.forwardRef((props, ref) => {
  const { type = 'text', name, valid, invalid, className, ...restProps } = props;

  return (
    <Field
      className={cn(
        'control',
        'c-auth-form-control',
        'appearance-none',
        'rounded-[5px]',
        'border',
        'border-solid',
        'border-neutral-lighter',
        'px-4',
        'py-2',
        'text-base',
        'bg-white',
        'text-neutral',
        'font-light',
        'block',
        'h-10',
        'box-border',
        'w-full',
        'transition-all',
        'duration-150',
        'hover:border-neutral',
        'focus:outline-none',
        'focus:border-primary',
        'disabled:text-neutral',
        'disabled:pointer-events-none',
        'disabled:opacity-25',
        valid && 'valid border-success',
        invalid && 'invalid border-danger',
        className
      )}
      type={type}
      name={name}
      innerRef={ref}
      {...restProps}
    />
  );
});

function FormLabel(props) {
  const { children, ...restProps } = props;

  return (
    <label
      className={cn('label', 'c-auth-form-label', 'font-bold', 'text-sm', 'text-primary')}
      {...restProps}
    >
      {children}
    </label>
  );
}

function FormRow(props) {
  const { children, disabled, ...restProps } = props;

  return (
    <div
      className={cn('row', 'c-auth-form-row', 'flex', 'flex-col', 'gap-1', disabled && 'disabled pointer-events-none opacity-80 [&_label]:text-neutral')}
      {...restProps}
    >
      {children}
    </div>
  );
}

function Form(props) {
  const { children, ...restProps } = props;

  return (
    <form
      className={cn('form', 'c-auth-form', 'flex', 'flex-col', 'gap-4')}
      {...restProps}
    >
      {children}
    </form>
  );
}

export { Form as default, FormRow, FormLabel, FormControl, FormField, FormLink, FormFeedback, FormStep, FormFieldGrade, FormFieldGradeWrapper, FormCustomSelect, FormTerms };
